import { GetCustomerModelMetadataResponse } from "@mutiny-group/sbo-openapi-client";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";

import { components } from "@/@types/api";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { axiosApi } from "@/Utils";

import { getCustomerModelMetadata } from "./v2/getCustomerModelMetadata";

export type ModelMetadataDTO = components["schemas"]["GetCustomerModelMetadataResponse"];
export type BrandGeographiesDTO = components["schemas"]["BrandGeographies"];

export const getModelMetadata = (): Promise<ModelMetadataDTO> => {
  return axiosApi.get(`/api/v2/scenarios-new/model-metadata`, {
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

type QueryFnType = typeof getModelMetadata | typeof getCustomerModelMetadata;

type UseModelMetadataOptions = {
  config?: QueryConfig<QueryFnType>;
  isScenariosApiEnabled?: boolean;
};

export const useModelMetadata = ({
  config,
  isScenariosApiEnabled = false,
}: UseModelMetadataOptions) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const { value: isNewBehaviorEnabled } = useFeatureGate(featureFlag.scenarioPortfolioDropdown);

  return useQuery({
    ...config,
    queryKey: [`model_metadata`],
    queryFn: isScenariosApiEnabled
      ? () => getCustomerModelMetadata({ scenarioBuilderClient })
      : () => getModelMetadata(),
    select: isNewBehaviorEnabled
      ? (data) => {
          const normalizedData = isScenariosApiEnabled
            ? convertModelMetadata(data as GetCustomerModelMetadataResponse)
            : (data as ModelMetadataDTO);

          const hasDefaultBrand = normalizedData.brand_geographies.some(
            (brand) => brand.brand === "default"
          );
          if (!hasDefaultBrand && normalizedData.brand_geographies.length > 1) {
            normalizedData.brand_geographies.unshift({
              brand: "default",
              geographies: [],
            });
          }
          return normalizedData;
        }
      : (data) => {
          return isScenariosApiEnabled
            ? convertModelMetadata(data as GetCustomerModelMetadataResponse)
            : (data as ModelMetadataDTO);
        },
  });
};

const convertModelMetadata = (data: GetCustomerModelMetadataResponse): ModelMetadataDTO => {
  return {
    geographies: data.geographies,
    brand_geographies: data.brandGeographies,
  };
};
